import axios from 'axios';
import { Capacitor } from '@capacitor/core';

// Determine the API base URL
// For mobile apps, use the production server URL
// For web app, use relative path
const getBaseURL = () => {
  const isMobileApp = Capacitor.isNativePlatform();
  // Only native mobile apps should use the production absolute URL
  if (isMobileApp) {
    // Mobile apps connect to production server
    const productionURL = 'https://onboarding.mibotsupport.co.za/api';
    console.log('📱 Mobile app connecting to:', productionURL);
    return productionURL;
  }
  // Web app uses relative path or environment variable
  return process.env.REACT_APP_API_BASE || '/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
  // For mobile apps, don't send cookies (they don't work cross-origin)
  // For web apps, send cookies for HTTP-only cookie authentication
  withCredentials: !Capacitor.isNativePlatform(),
});

function getCookie(name) {
  const value = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return value ? decodeURIComponent(value.split('=')[1]) : '';
}

api.interceptors.request.use(
  (config) => {
    // For mobile apps, use BOTH Authorization header AND CSRF token from localStorage
    if (Capacitor.isNativePlatform()) {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
        console.log('📱 Sending request with auth token');
      } else {
        console.log('📱 No auth token found in mobile app');
      }
      
      // ALSO send CSRF token for mobile (production server requires it)
      const csrfToken = localStorage.getItem('csrfToken'); // Get from localStorage
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken;
        console.log('📱 Sending CSRF token from localStorage:', csrfToken.substring(0, 10) + '...');
      } else {
        console.log('📱 No CSRF token found in localStorage');
      }
    } else {
      // For web apps, use CSRF token from cookies
      const csrfToken = getCookie('csrfToken');
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let refreshPromise = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Rate limiting should never log the user out; just surface the error to the UI.
    if (status === 429) {
      return Promise.reject(error);
    }

    // Attempt a single refresh on 401 before redirecting
    if (
      status === 401 &&
      !originalRequest?._retry &&
      !originalRequest?.url?.includes('/auth/login') &&
      !originalRequest?.url?.includes('/auth/refresh')
    ) {
      originalRequest._retry = true;
      if (!refreshPromise) {
        refreshPromise = api.post('/auth/refresh').finally(() => {
          refreshPromise = null;
        });
      }
      try {
        await refreshPromise;
        return api(originalRequest);
      } catch {
        // fall through to logout flow
      }
    }

    if (status === 401 || status === 403) {
      const isLoginPage = window.location.pathname.includes('/login') || window.location.pathname === '/';
      const isLoginRequest = originalRequest?.url?.includes('/auth/login');
      
      if (!isLoginPage && !isLoginRequest) {
        if (status === 401) {
          if (Capacitor.isNativePlatform()) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('csrfToken'); // Clear CSRF token too
            console.log('📱 Auth and CSRF tokens cleared due to 401 Unauthorized');
          }
          window.location.href = '/';
        } else if (status === 403) {
          console.warn('📱 403 Forbidden: User does not have permission for this action');
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
